import { useRecoilValue } from "recoil";
import { Box, Grid, Stack, Typography } from "@mui/joy";
import React, { useCallback, useEffect, useState } from "react";
import ThoughtMap from "components/thoughtmap/ThoughtMap";
import TestResult from "components/dashboard/TestResult";
import History from "components/dashboard/History";
import { patientAtom } from "recoil/patientAtom";
import {
  dashboardApi,
  DashboardType,
  DASHBOARD_DETAIL_ENDPOINT,
  DASHBOARD_DOMAIN,
} from "api/dashboardApi";
import { useQuery } from "@tanstack/react-query";
import { layoutAtom } from "recoil/layoutAtom";

function Title({ title, subtitle }: { title: string; subtitle?: string }) {
  const customProps = {
    endDecorator: (
      <Typography component="span" level="body-xs" sx={{ ml: 1, opacity: 0.5 }}>
        {subtitle}
      </Typography>
    ),
  };
  return (
    <Typography level="title-lg" {...(subtitle && customProps)}>
      {title}
    </Typography>
  );
}
function Section({
  title,
  subtitle,
  content,
}: {
  title: string;
  subtitle?: string;
  content: React.ReactNode;
}) {
  return (
    <Stack spacing={1} sx={{ height: "100%" }}>
      <Title title={title} subtitle={subtitle} />
      <Box sx={{ height: "100%" }}>{content}</Box>
    </Stack>
  );
}

export default function DashboardContent() {
  const { isTaskContainerSmall } = useRecoilValue(layoutAtom);
  const patientState = useRecoilValue(patientAtom);

  const queryFn = useCallback(
    () => dashboardApi.load(`${patientState?.patientId}`),
    [patientState]
  );
  const [dashboardData, setDashboardData] = useState<DashboardType>();
  useQuery([DASHBOARD_DOMAIN, DASHBOARD_DETAIL_ENDPOINT], queryFn, {
    enabled: !!patientState?.patientId,
    onSuccess: (data) => {
      setDashboardData(data.result);
    },
  });

  useEffect(() => {
    if (!patientState?.patientId) {
      setDashboardData({
        concept: {},
        history: {},
        selfCheckResult: {},
      });
    }
  }, [patientState]);

  return (
    <Box className=" h-full w-full overflow-auto">
      {dashboardData && (
        <Stack
          spacing={2}
          className=" h-full w-full max-w-[960px] mx-auto py-[36px] px-[24px] flex flex-col"
        >
          <Grid
            container
            spacing={2}
            alignItems="stretch"
            sx={{ flexGrow: 1, width: "100%" }}
          >
            <Grid xs={isTaskContainerSmall ? 12 : 8}>
              <Section
                title={"생각 지도"}
                subtitle={"코치 선생님과 함께 찾아 채워나가요."}
                content={<ThoughtMap data={dashboardData?.concept} />}
              />
            </Grid>
            <Grid xs={isTaskContainerSmall ? 12 : 4}>
              <Section
                title={"히스토리"}
                content={<History {...dashboardData?.history} />}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            sx={{ flexGrow: 1, width: "100%", pb: "36px" }}
          >
            <Grid xs={12}>
              <Section
                title={"부정적 반응"}
                subtitle={"2주마다 측정하며 경과를 지켜봐요."}
                content={<TestResult {...dashboardData.selfCheckResult} />}
              />
            </Grid>
          </Grid>
        </Stack>
      )}
    </Box>
  );
}
